import { ReactNode, useEffect, useState } from 'react'
import usePreferences from '@/usePreferences.ts'
import { ThemeProviderContext } from '@/useTheme.ts'
import { Capacitor } from '@capacitor/core'
import { Config, SafeArea } from '@capacitor-community/safe-area'
import { Keyboard } from '@capacitor/keyboard'

export type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
	children: ReactNode
	defaultTheme?: Theme
	storageKey?: string
}

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'vite-ui-theme',
	...props
}: ThemeProviderProps) {
	const { getItem, setItem } = usePreferences()
	const [theme, setTheme] = useState<Theme>(defaultTheme)
	const [themeColor, setThemeColor] = useState<string>('light')

	useEffect(() => {
		const applyTheme = async () => {
			const storedTheme = await getItem(storageKey)
			const activeTheme = storedTheme || defaultTheme

			if (theme !== activeTheme) {
				setTheme(activeTheme as Theme)
			}

			const root = window.document.documentElement
			root.classList.remove('light', 'dark')

			let currentTheme = null
			if (activeTheme === 'system') {
				const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
				currentTheme = systemTheme
				root.classList.add(systemTheme)
			} else {
				currentTheme = activeTheme
				root.classList.add(activeTheme)
			}
			setThemeColor(currentTheme)

			if (Capacitor.isPluginAvailable('StatusBar')) {
				const config: Config = {
					customColorsForSystemBars: true,
					offset: Capacitor.getPlatform() === 'ios' ? 5 : 8,
				}
				// await SafeArea.enable({
				// 	config: {
				// 		navigationBarColor: '#00000000', // transparent
				// 		navigationBarContent: 'light',
				// 	},
				// })
				if (currentTheme == 'dark') {
					config.statusBarContent = 'light'
					config.statusBarColor = '#121212'
				} else {
					config.statusBarContent = 'dark'
					config.statusBarColor = '#ffffff'
				}
				await SafeArea.enable({ config })
			}
			if (Capacitor.isPluginAvailable('Keyboard')) {
				await Keyboard.setAccessoryBarVisible({
					isVisible: false,
				})
			}
		}

		applyTheme()
	}, [getItem, setTheme, theme, defaultTheme, setThemeColor])

	const value = {
		appTheme: theme,
		setAppTheme: async (theme: Theme) => {
			await setItem(storageKey, theme)
			setTheme(theme)
		},
		themeColor: themeColor,
		setThemeColor: (color: string) => setThemeColor(color),
	}

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	)
}
